import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LoaderDefault } from '@vr/lib';
import MapaComposition from './compositions/Mapa';
import AppProvider from './providers/AppProvider';
import injectScript from './helpers/injectScript';
import { GOOGLE_MAPS_KEY, VRPAT } from './config/constants';

/**
 * Componente Mapa
 * Principal componente da lib, onde trará toda a funcionalidade de mapa.
 * Exemplo:
 * ```js
 * import React from 'react';
 * import { Mapa } from '@vr/mapa';
 *
 * function App() {
 *  const listaEstabelecimentos = [];
 *  const produtos = ['00027', '00204'];
 *
 *  function handleSearch(filter) {
 *    // Chamada ao serviço onde será realizado a busca dos estabelecimentos e atualiza a lista de estabelecimentos.
 *  }
 *
 *  return (
 *    <Mapa
 *      findEstablishment={handleSearch}
 *      locations={listaEstabelecimentos}
 *      products={produtos}
 *    />
 *  );
 * }
 * ```
 *
 */

function Mapa({ loadGooogleMapsScript, apiKey, onFailure, issuer, ...rest }) {
  const [loadingGoogleMaps, setLoadingGoogleMaps] = useState(true);

  useEffect(() => {
    async function init() {
      setLoadingGoogleMaps(true);
      try {
        if (loadGooogleMapsScript) {
          await injectScript(apiKey || GOOGLE_MAPS_KEY);
        }
      } catch (e) {
        onFailure('Não foi possível carregar a API do Google Maps');
      } finally {
        setLoadingGoogleMaps(false);
      }
    }

    init();
  }, [loadGooogleMapsScript, apiKey, onFailure]);

  return (
    <AppProvider issuer={issuer}>
      {loadingGoogleMaps ? <LoaderDefault /> : <MapaComposition {...rest} />}
    </AppProvider>
  );
}

Mapa.propTypes = {
  /**
   * Essa propriedade deve receber uma função que será executada quando o usuário clicar na opção de buscar.
   */
  findEstablishment: PropTypes.func.isRequired,
  /**
   * Esta propriedade recebe um array de objetos contendo os estabelecimentos que deverão ser exibidos no mapa.
   */
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      latitude: PropTypes.string.isRequired,
      longitude: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /**
   * Esta propriedade receberá um array com os códigos dos produtos disponíveis para seleção
   */
  products: PropTypes.arrayOf(PropTypes.any).isRequired,
  /**
   * Esta propriedade define se a caixa de pesquisa será carregada aberta ou fechada
   */
  open: PropTypes.bool,
  /**
   * Função para customizar o conteúdo que será exibido na caixa de seleção de produtos
   */
  renderOption: PropTypes.func,
  /**
   * DEPRECATED: Título a ser exibido na caixa de busca
   */
  searchTitle: PropTypes.string,
  /**
   * Indicação de loading. Será apresentado o LoaderDefault da @vr/lib
   */
  isLoading: PropTypes.bool,
  /**
   * Irá exibir a página com o resumo de resultados
   */
  showResumoResultados: PropTypes.bool,
  /**
   * Configuração adicional do Google Maps
   */
  googleMapsConfig: PropTypes.shape({}),
  /**
   * Valores default a serem inseridos no formulário
   */
  defaultValues: PropTypes.shape({}),
  /**
   * Define a altura do mapa
   */
  height: PropTypes.string,
  /**
   * Define se o mapa deve carregar o script do google maps por padrão ou não.
   * Desabilitando essa opção o usuário deve realizar a inserção do script do Google Maps manualmente na página
   */
  loadGooogleMapsScript: PropTypes.bool,
  /**
   * Chave do Google Maps
   */
  apiKey: PropTypes.string,
  /**
   * Função que será executada quando ocorrer algum erro ao carregar o script do Google Maps
   */
  onFailure: PropTypes.func,
  /**
   * Código do emissor. Caixa (JVCEF) ou VR (VRPAT)
   */
  issuer: PropTypes.string,
  /**
   * Prefixo do nome da empresa a ser exibido nos nomes de produtos
   * default: vazio
   */
  companyName: PropTypes.string,
};

Mapa.defaultProps = {
  open: false,
  renderOption: null,
  searchTitle: null,
  isLoading: false,
  showResumoResultados: false,
  googleMapsConfig: {},
  defaultValues: {},
  height: '100vh',
  loadGooogleMapsScript: true,
  apiKey: null,
  // eslint-disable-next-line no-console
  onFailure: console.error,
  issuer: VRPAT,
  companyName: '',
};

export default Mapa;
